import React, { useState, useEffect, startTransition } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function DailyDashboard({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [loading, setLoading] = useState(false);
  const [currentData, setCurrentData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // All table names to query
  const tableNames = [
    'squad_41', 'squad_42', 'squad_43',
    'engine_41', 'engine_42', 'engine_43', 
    'station_41', 'station_42', 'station_43',
    'ems_captain'
  ];

  // Get today's date for filtering
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayString = today.toLocaleDateString();

  // Format substance name helper
  const formatSubstanceName = (substance) => {
    const nameMap = {
      'fentanyl_100mcg_2ml': 'Fentanyl 100mcg/2mL',
      'ketamine_500mg_10ml': 'Ketamine 500mg/10mL', 
      'morphine_10mg_1ml': 'Morphine 10mg/1mL',
      'diazepam_10mg_2ml': 'Diazepam 10mg/2mL',
      'midazolam_10mg_2ml': 'Midazolam 10mg/2mL'
    };
    return nameMap[substance] || substance;
  };

  // Load dashboard data
  const loadDashboard = async () => {
    setLoading(true);
    setCurrentData([]);

    try {
      // Fetch from all tables simultaneously
      const fetchPromises = tableNames.map(async (tableName) => {
        try {
          const { data, error } = await supabase
            .from(tableName)
            .select('*')
            .order('created_at', { ascending: false });

          if (error) {
            console.error(`Error fetching from ${tableName}:`, error);
            return [];
          }
          
          return (data || []).map(record => ({ ...record, source_table: tableName }));
        } catch (err) {
          console.error(`Error with table ${tableName}:`, err);
          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const allEntries = results.flat();

      // Filter for today's entries and format data
      const todaysData = allEntries
        .filter(entry => {
          // Parse date field (MM/DD/YYYY format)
          const dateParts = (entry.date || '').split('/');
          let entryDate;
          
          if (dateParts.length === 3) {
            // MM/DD/YYYY format
            entryDate = new Date(+dateParts[2], +dateParts[0] - 1, +dateParts[1]);
          } else {
            // Fallback to created_at
            entryDate = new Date(entry.created_at || 0);
          }
          
          return entryDate.toDateString() === today.toDateString();
        })
        .map(entry => {
          // Parse date for display
          const dateParts = (entry.date || '').split('/');
          let entryDate;
          
          if (dateParts.length === 3) {
            entryDate = new Date(+dateParts[2], +dateParts[0] - 1, +dateParts[1]);
          } else {
            entryDate = new Date(entry.created_at || 0);
          }

          const timeStr = entry.time || 
            (entry.created_at ? new Date(entry.created_at).toLocaleTimeString() : '');

          // Determine apparatus name from source table and location
          let apparatus = entry.location || 'Unknown';
          if (entry.source_table) {
            const tableMap = {
              'squad_41': 'Squad 41', 'squad_42': 'Squad 42', 'squad_43': 'Squad 43',
              'engine_41': 'Engine 41', 'engine_42': 'Engine 42', 'engine_43': 'Engine 43',
              'station_41': 'Station 41', 'station_42': 'Station 42', 'station_43': 'Station 43',
              'ems_captain': 'EMS Captain Office'
            };
            apparatus = tableMap[entry.source_table] || entry.location || 'Unknown';
          }

          // Format logged by name
          const loggedBy = `${entry.last_name || ''} ${entry.first_name || ''}`.trim() ||
                          `${entry.lastName || ''} ${entry.firstName || ''}`.trim() ||
                          'Unknown';

          // Format narcotics - handle both current quantities and begin/end for stations
          const substances = [
            'fentanyl_100mcg_2ml', 'ketamine_500mg_10ml', 'morphine_10mg_1ml', 
            'diazepam_10mg_2ml', 'midazolam_10mg_2ml'
          ];

          const narcoticsInfo = [];
          
          substances.forEach(substance => {
            let quantity = 0;
            
            // Check for regular quantity field first
            if (entry[substance] && parseFloat(entry[substance]) > 0) {
              quantity = parseFloat(entry[substance]);
            }
            // Check for ending quantity (stations use begin/end)
            else if (entry[substance + '_end'] && parseFloat(entry[substance + '_end']) > 0) {
              quantity = parseFloat(entry[substance + '_end']);
            }
            // Check for beginning quantity if no ending
            else if (entry[substance + '_begin'] && parseFloat(entry[substance + '_begin']) > 0) {
              quantity = parseFloat(entry[substance + '_begin']);
            }
            
            if (quantity > 0) {
              narcoticsInfo.push(`(${quantity}) ${formatSubstanceName(substance)}`);
            }
          });

          return {
            apparatus,
            logDateTime: `${entryDate.toLocaleDateString()} ${timeStr}`,
            loggedBy,
            narcoticsTypes: narcoticsInfo.join(', ') || 'No narcotics recorded',
            rawDateTime: new Date(`${entryDate.toDateString()} ${timeStr}`)
          };
        })
        .sort((a, b) => a.rawDateTime - b.rawDateTime); // Sort chronologically

      setCurrentData(todaysData);

    } catch (error) {
      console.error('Error loading dashboard:', error);
      alert('Error loading dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedData = [...currentData].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setCurrentData(sortedData);
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Hamburger Menu */}
      <HamburgerMenu 
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      {/* Dashboard Content */}
      <div style={{ padding: '20px', paddingTop: '70px' }}>
        {/* Header */}
        <div style={{ 
          background: 'white',
          borderRadius: '15px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <h2 style={{ 
                fontSize: '28px', 
                color: '#333', 
                marginBottom: '15px', 
                fontWeight: '600' 
              }}>
                Admin Dashboard - Daily Snapshot
              </h2>
              <p style={{ 
                color: '#666', 
                fontSize: '16px', 
                lineHeight: '1.5', 
                marginBottom: '15px' 
              }}>
                The "Admin Dashboard - Daily Snapshot" provides administrators with a quick overview of daily inventory log activities for all ambulances, including who logged them and the narcotics recorded with quantities. Logs are displayed in chronological order for efficient tracking.
              </p>
              <p style={{ 
                color: '#333', 
                fontSize: '16px', 
                fontWeight: '600' 
              }}>
                Date: {todayString}
              </p>
            </div>
            <button 
              onClick={loadDashboard}
              disabled={loading}
              className="btn btn-primary"
              style={{ 
                minWidth: '140px',
                alignSelf: 'flex-start'
              }}
            >
              {loading ? 'Loading...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: '15px',
            marginBottom: '20px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <div className="spinner" style={{ fontSize: '24px' }}>⟲</div>
            <p style={{ marginTop: '10px', color: '#666' }}>
              Loading Dashboard... This may take up to 2 minutes.
            </p>
          </div>
        )}

        {/* Data Table */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th 
                    onClick={() => handleSort('apparatus')}
                    style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#333',
                      userSelect: 'none'
                    }}
                  >
                    Apparatus{getSortIndicator('apparatus')}
                  </th>
                  <th 
                    onClick={() => handleSort('logDateTime')}
                    style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#333',
                      userSelect: 'none'
                    }}
                  >
                    Log Date/Time{getSortIndicator('logDateTime')}
                  </th>
                  <th 
                    onClick={() => handleSort('loggedBy')}
                    style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#333',
                      userSelect: 'none'
                    }}
                  >
                    Logged By{getSortIndicator('loggedBy')}
                  </th>
                  <th 
                    onClick={() => handleSort('narcoticsTypes')}
                    style={{ 
                      padding: '15px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: '#333',
                      userSelect: 'none'
                    }}
                  >
                    Narcotic Info{getSortIndicator('narcoticsTypes')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loading && currentData.length === 0 ? (
                  <tr>
                    <td 
                      colSpan="4" 
                      style={{ 
                        textAlign: 'center', 
                        color: '#666', 
                        padding: '40px',
                        fontStyle: 'italic'
                      }}
                    >
                      No logs found for today.
                    </td>
                  </tr>
                ) : (
                  currentData.map((item, index) => (
                    <tr 
                      key={index}
                      style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': { backgroundColor: '#f9f9f9' }
                      }}
                      onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f9f9f9'}
                      onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                    >
                      <td style={{ 
                        padding: '12px 15px', 
                        color: '#333',
                        fontWeight: '500'
                      }}>
                        {item.apparatus}
                      </td>
                      <td style={{ 
                        padding: '12px 15px', 
                        color: '#333'
                      }}>
                        {item.logDateTime}
                      </td>
                      <td style={{ 
                        padding: '12px 15px', 
                        color: '#333'
                      }}>
                        {item.loggedBy}
                      </td>
                      <td style={{ 
                        padding: '12px 15px', 
                        color: '#333',
                        fontSize: '13px',
                        lineHeight: '1.4'
                      }}>
                        {item.narcoticsTypes}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Info */}
        {!loading && currentData.length > 0 && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#666',
            fontSize: '14px'
          }}>
            Showing {currentData.length} log{currentData.length !== 1 ? 's' : ''} for {todayString}
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyDashboard;